/**
 * TermsEn - the terms of service, in English
 * -------------------------------------------
 * The English half. `TermsIt.jsx` is the Italian one and `Terms.jsx` picks
 * between them; the page furniture lives there.
 *
 * THE TWO FILES MUST SAY THE SAME THING. Change one and change the other in
 * the same commit, and bump TERMS_VERSION in backend/config/features.js.
 *
 * THE SECTION THAT MATTERS MOST IS 3. Making it unmistakable that this is an
 * introduction service - that we do not verify, screen, supervise, or vouch
 * for anybody - is what keeps ChoreConnect from being treated as an employer
 * or an agency, and it is the clause a claim would turn on.
 *
 * WRITTEN IN PLAIN ENGLISH ON PURPOSE. A sixteen-year-old has to be able to
 * read this, and the Garante expects that of a service aimed at minors. Plain
 * does not mean vague: every term that carries weight is defined in section 1.
 *
 * A NOTE ON WHAT THIS CANNOT DO. Italian consumer law (Codice del Consumo,
 * arts. 33-38) voids unfair terms. A clause excluding liability for death or
 * personal injury, or for our own serious fault, is not merely ineffective -
 * it is struck out, and it makes the rest look drafted in bad faith. So the
 * liability section claims what the law allows and no more.
 */

import { Link } from 'react-router-dom';

const APP_NAME = import.meta.env.VITE_APP_NAME || 'ChoreConnect';
const SAFETY_EMAIL = 'safety@choreconnect.org';

export default function TermsEn() {
  return (
    <>
      <div className="card">
        <h2 style={{ fontSize: 'var(--text-h3)' }}>The short version</h2>
        <p style={{ marginTop: 'var(--space-xs)' }}>
          {APP_NAME} introduces people who need a small job done to students who want to do one.
          We are not an employer, we do not check who anybody is, we do not watch the work, and we
          do not handle the money. The agreement to do a job is between the two of you. Be honest,
          be careful, and tell us if something goes wrong.
        </p>
        <p className="text-sm text-muted" style={{ marginTop: 'var(--space-xs)' }}>
          The rest of this page is the detail. It matters - please read it.
        </p>
      </div>

      <hr className="divider" />

      {/* ============================================================= */}
      <h2>Who runs {APP_NAME}</h2>
      <p>
        {APP_NAME} is run by <strong>Evelyn Catelijne Brouwer</strong>, Via Guglielmo Marconi 249,
        21027 Ispra (VA), Italy. There is no company behind it. You can write to{' '}
        <a href={`mailto:${SAFETY_EMAIL}`}>{SAFETY_EMAIL}</a> about anything on this page, and the
        privacy notice says the same thing about your information.
      </p>

      {/* ============================================================= */}
      <h2>1. What the words mean</h2>
      <p>
        These terms use a few words in a particular way. Wherever they appear, this is what they
        mean.
      </p>
      <ul style={{ marginLeft: 'var(--space-md)' }}>
        <li>
          <strong>{APP_NAME}</strong>, <strong>we</strong>, <strong>us</strong> - the website at
          choreconnect.org and the service it provides.
        </li>
        <li>
          <strong>User</strong>, <strong>you</strong> - anybody with an account.
        </li>
        <li>
          <strong>Poster</strong> - an adult user who posts a chore.
        </li>
        <li>
          <strong>Student</strong> - a user aged 16 or over who is at a school we support, and who
          may apply for chores.
        </li>
        <li>
          <strong>Chore</strong> - a single, specific job a poster describes on the site, with a
          date, a place and a payment.
        </li>
        <li>
          <strong>Offer</strong> (also called <em>availability</em>) - a student saying what they
          can help with and which times of the week they are usually free.
        </li>
        <li>
          <strong>Backup</strong> - a second student a poster accepts in case the first cannot go.
        </li>
        <li>
          <strong>Content</strong> - anything you write, upload or send through the site: chore
          descriptions, offers, profile text, photographs, reviews and messages.
        </li>
        <li>
          <strong>Moderator</strong> - a person we have given the ability to read reports, act on
          them, and remove content.
        </li>
        <li>
          <strong>Dangerous work</strong> - defined in section 8.
        </li>
      </ul>

      {/* ============================================================= */}
      <h2>2. Who can use {APP_NAME}</h2>
      <ul style={{ marginLeft: 'var(--space-md)' }}>
        <li>
          <strong>Students must be at least 16</strong>, the minimum working age in Italy. We ask
          your date of birth and we take your answer at face value - see section 3. It cannot be
          changed from your account once you have signed up; if it is wrong, tell us.
        </li>
        <li>
          <strong>Students under 18 need a parent or guardian to confirm by email</strong> before
          applying for work, publishing availability, or being booked. Nothing happens until they
          do. From a student&apos;s eighteenth birthday this is no longer needed.
        </li>
        <li>
          <strong>Posters must be 18 or over.</strong>
        </li>
        <li>
          <strong>Students must be at a school we support</strong> and have its joining code.
        </li>
        <li>
          <strong>Posters, and students who sign up aged 18 or over, must confirm their email
          address</strong>{' '}
          before posting a chore, applying for one, publishing availability, or asking a student
          about a time. You can browse before that.
        </li>
        <li>
          <strong>One account each</strong>, in your own name, with details that are true.
        </li>
      </ul>
      <p>
        You are responsible for what happens through your account and for keeping your password to
        yourself. Tell us if you think somebody else has got into it.
      </p>

      {/* ============================================================= */}
      <h2>3. What we are, and what we are not</h2>
      <p>
        <strong>{APP_NAME} is an introduction service and nothing more.</strong> We show people who
        need a job done alongside students who want to do one. Everything after the introduction is
        between the two of you.
      </p>

      <h3>We do not check anybody</h3>
      <p>This is the most important paragraph on the page. Please do not skim it.</p>
      <ul style={{ marginLeft: 'var(--space-md)' }}>
        <li>
          <strong>We do not verify anybody&apos;s identity.</strong> We do not ask for or check any
          document. A name on this site is a name somebody typed.
        </li>
        <li>
          <strong>We do not carry out background checks or criminal record checks</strong> on
          anybody, of any kind, ever.
        </li>
        <li>
          <strong>We do not verify ages.</strong> A student&apos;s age comes from the date of birth
          they gave us.
        </li>
        <li>
          <strong>We do not verify addresses</strong>, or check that a place is what it was said to
          be, or that it is safe.
        </li>
        <li>
          <strong>We do not check references</strong>, qualifications, insurance, or the right to
          work.
        </li>
        <li>
          <strong>We do not check that a parent&apos;s email address really belongs to a
          parent.</strong> We send a message to the address a student gives us and rely on the
          answer.
        </li>
        <li>
          <strong>Reviews and star ratings are opinions written by users.</strong> They are not
          checked by us and are not a recommendation.
        </li>
      </ul>
      <p>
        <strong>Treat every user as a stranger, because that is what they are.</strong> If you would
        not do something with a stranger, do not do it because of anything you read here.
      </p>

      <h3>We do not supervise the work</h3>
      <ul style={{ marginLeft: 'var(--space-md)' }}>
        <li>We are not present. We do not inspect any place of work before, during or after.</li>
        <li>
          We do not check that a chore is safe, lawful, suitable for a minor, or described
          accurately.
        </li>
        <li>We do not check that work was done, or done properly.</li>
        <li>We do not check that anybody was paid.</li>
        <li>
          We do not check whether anybody has met their obligations for tax, social security,
          INPS, INAIL or insurance - see section 9.
        </li>
      </ul>

      <h3>We are not</h3>
      <ul style={{ marginLeft: 'var(--space-md)' }}>
        <li>an employer, a staffing agency or an employment intermediary</li>
        <li>a party to any agreement you make with another user</li>
        <li>an agent for either of you</li>
        <li>involved in payment - money is arranged directly between you</li>
        <li>a guarantor of anybody&apos;s conduct, honesty, skill or safety</li>
      </ul>
      <p>
        <strong>The agreement to do a job is between the poster and the student.</strong> If it goes
        wrong, that is between the two of you - though we would still like to hear about it, and
        section 12 says how.
      </p>

      {/* ============================================================= */}
      <h2>4. What you must not do</h2>
      <ul style={{ marginLeft: 'var(--space-md)' }}>
        <li>Break the law, or use the site to arrange anything unlawful.</li>
        <li>Lie about who you are, your age, or what a job involves.</li>
        <li>Use somebody else&apos;s account, or let anybody use yours.</li>
        <li>Post anything abusive, threatening, discriminatory, sexual, or harassing.</li>
        <li>
          Contact a student for any purpose other than a chore on this site, or ask a student for
          personal contact details in order to move the conversation somewhere nobody can see it.
        </li>
        <li>Ask a student to do dangerous work (section 8).</li>
        <li>Post anybody else&apos;s personal information, or a photograph of anybody else.</li>
        <li>Advertise, recruit, or use the site to sell anything.</li>
        <li>
          Collect data from the site automatically, try to get round its security, or interfere with
          how it runs.
        </li>
        <li>Post anything you do not have the right to post.</li>
      </ul>

      {/* ============================================================= */}
      <h2>5. Your content</h2>
      <p>
        <strong>What you write stays yours.</strong> By posting it you give us permission to store
        it, display it to the people it is meant for, and keep it as described in our{' '}
        <Link to="/privacy">privacy notice</Link>. That permission is free, and lasts as long as we
        need it to run the site and keep the records described there. It exists so that we can show
        your chore to students, or your review to other users, and nothing else - we do not sell it,
        licence it on, or use it to advertise.
      </p>
      <p>
        <strong>You are responsible for what you post</strong>, and you confirm that you have the
        right to post it and that it is accurate.
      </p>
      <p>
        <strong>Reviews</strong> must be honest, about work that actually happened, and about the
        work rather than the person. No abuse and no personal details.
      </p>
      <p>
        <strong>A student starts every conversation about a chore.</strong> A student can ask about
        a chore that is open for applications, and the two people on an agreed job can message each
        other about it. Somebody who posts a chore can never message a student who has not written
        to them first.
      </p>
      <p>
        <strong>An offer works the other way round, and is limited because of it.</strong> A student
        who publishes when they are free can be sent <strong>one question</strong> about it by an
        adult - and nothing more unless the student answers. The student decides whether to answer,
        answering is what opens the conversation, and ignoring it leaves the adult with no way to
        write again. An adult may have five unanswered questions out at a time across the whole
        site. Taking the offer down ends the conversation.
      </p>
      <p>
        <strong>Messages are not private from us.</strong> They are stored, they are never deleted,
        and a moderator can read a conversation if it is reported or if our system notices contact
        details being shared. The privacy notice explains exactly when. This is deliberate: it is
        what makes it possible to look into a complaint about something said in private.
      </p>

      {/* ============================================================= */}
      <h2>6. Doing a chore</h2>
      <h3>Everybody</h3>
      <ul style={{ marginLeft: 'var(--space-md)' }}>
        <li>Agree the work, the time and the payment before anything starts.</li>
        <li>Turn up when you said you would, or give proper notice.</li>
        <li>Treat the other person decently.</li>
      </ul>

      <h3>Posters</h3>
      <ul style={{ marginLeft: 'var(--space-md)' }}>
        <li>Describe the job honestly, including anything heavy, dirty or awkward.</li>
        {/* Added 16 September 2026, when the street address became required
            on every chore. */}
        <li>
          Give the real address where the work is. Only the student you accept and your backup
          can see it.
        </li>
        <li>Provide a safe place to work, and say what you expect them to bring.</li>
        <li>Pay what you agreed, when you agreed.</li>
        <li>
          <strong>Remember you may be dealing with a sixteen-year-old.</strong> Do not ask a student
          to be alone with you somewhere they cannot leave, do not offer alcohol, and do not ask
          them to do anything you would not ask in front of their parent.
        </li>
      </ul>

      <h3>Students</h3>
      <ul style={{ marginLeft: 'var(--space-md)' }}>
        <li>Only apply for work you can genuinely do safely.</li>
        <li>
          <strong>Tell somebody at home where you are going and when you will be back.</strong>
        </li>
        <li>Give at least 24 hours&apos; notice if you cannot make it.</li>
      </ul>

      {/* ============================================================= */}
      <h2>7. You can always say no</h2>
      <p>
        This section is for students, and it is the most important thing on this page for you.
      </p>
      <p>
        <strong>Nothing on {APP_NAME} obliges you to walk into a situation you do not
        trust.</strong> Not the fact that you applied, not the fact that you agreed, not the fact
        that somebody is waiting, and not the 24-hour rule.
      </p>
      <ul style={{ marginLeft: 'var(--space-md)' }}>
        <li>
          <strong>You do not have to go in.</strong> If the place, the person or the situation is
          not what you expected when you arrive - more people than you were told, a different
          address, anything that feels wrong - turn round and leave. You never have to knock.
        </li>
        <li>
          <strong>You can leave part-way through</strong>, at any point, without finishing and
          without giving a reason.
        </li>
        <li>
          <strong>You never owe the person whose job it is an explanation</strong>, and you do not
          have to stay to be polite. Being polite is not worth your safety, and anybody decent
          would rather you left. Telling <em>us</em> what happened afterwards is a different thing,
          and section 7 below sets out when we ask for it - it is never shown to them.
        </li>
        <li>
          <strong>Trust the feeling.</strong> You do not need to be able to point at what is wrong,
          and you do not need to be right.
        </li>
      </ul>
      <p>
        <strong>Use the &ldquo;I do not feel safe&rdquo; button on your dashboard.</strong> It works
        inside the 24 hours too, and unlike a late cancellation it leaves nothing on your record at
        all. When you use it:
      </p>
      <ul style={{ marginLeft: 'var(--space-md)' }}>
        <li>
          <strong>It does not count against you.</strong> It is not a cancellation, and nothing
          appears on your profile or your record.
        </li>
        <li>
          <strong>The other person is not told that you reported anything.</strong> They are only
          told the job is free again. We do it that way in case you are still nearby.
        </li>
        <li>
          <strong>You do have to tell us what happened</strong> - a few sentences, before you
          leave. A moderator reads it the same day, and it is all they have to go on. It is never
          shown to the person who posted the job.
        </li>
        <li>
          <strong>It is for feeling unsafe, and not for changing your mind.</strong> Using it to
          get out of a job you simply do not want is a breach of these terms and may lead to your
          account being suspended. If you cannot make it, cancel the job and say why - that is
          what cancelling is for, and nobody is in trouble for it.
        </li>
      </ul>
      <p>
        <strong>If you are in danger right now, call 112 first.</strong> Everything here can wait
        until afterwards.
      </p>
      <p className="text-sm text-muted">
        None of this makes you responsible for your own safety in a way that lets anybody else off.
        A poster who puts you in that position has broken these terms, whatever you did about it.
      </p>

      {/* ============================================================= */}
      <h2>8. Dangerous work</h2>
      <p>
        <strong>You must not ask a student to do dangerous work, and a student must not agree to
        it.</strong> Italian law protects people under 18 from particular kinds of work - see
        D.Lgs. 345/1999 and the annex to D.Lgs. 81/2008. Because &ldquo;dangerous&rdquo; is easy to
        argue about, here is what it means on this site. It includes, and is not limited to:
      </p>
      <ul style={{ marginLeft: 'var(--space-md)' }}>
        <li>work at height - ladders, roofs, gutters, anything above standing reach</li>
        <li>power tools, chainsaws, ride-on mowers, or industrial machinery</li>
        <li>
          chemicals beyond ordinary household cleaning products used as the label says - pesticides,
          solvents, strong acids or alkalis
        </li>
        <li>electrical work, gas work or plumbing beyond changing a bulb or a battery</li>
        <li>lifting anything heavy enough to need two people, or repeated heavy lifting</li>
        <li>work with fire, hot surfaces, or anything that could burn</li>
        <li>demolition, construction, or anything structural</li>
        <li>handling waste that is sharp, medical or hazardous</li>
        <li>work with animals that could reasonably injure somebody</li>
        <li>driving any vehicle</li>
        <li>work in confined spaces, or anywhere they could not get out unaided</li>
        <li>work at night, meaning between 22:00 and 06:00</li>
      </ul>
      <p>
        <strong>If in doubt, do not.</strong> A poster who asks for any of this is breaking these
        terms, and we will remove the chore and may close the account. A student asked to do any of
        it should refuse, leave, and tell us.
      </p>

      {/* ============================================================= */}
      <h2>9. Cancelling</h2>
      <p>
        A student who has accepted a job should cancel at least 24 hours beforehand wherever they
        can. <strong>Inside 24 hours you may still cancel, but you have to say why</strong>, and
        what you write is shown to the person who posted the job. It still counts as a late
        cancellation on your record. Repeatedly letting people down may cost you your account.
      </p>
      <p>
        A poster who cancels should give as much notice as they can. A student who has travelled to
        a job that was cancelled without notice is owed the courtesy of being told why.
      </p>

      {/* Added 24 August 2026 with the change that locks an agreed job. Both
          halves are enforced in code - LOCKED_ONCE_ASSIGNED in
          backend/routes/chores.js refuses the edit, and datesMovedAfter in
          backend/models/Chore.js is the whole of the student's right. Do not
          soften the wording here without changing those. */}
      <h3>Changing a job somebody has already agreed to</h3>
      <p>
        Once a student has accepted, <strong>the job itself is fixed</strong>. The poster can no
        longer change the title, the description, the category, the pay, the address or how long it
        takes - the student weighed up that job and agreed to it, and a different job needs a
        different agreement. If the work has genuinely changed, the poster cancels and posts the
        new job.
      </p>
      <p>
        <strong>The day and the time can still be moved</strong>, because plans change and it is
        better done on the site than privately. But moving them has a cost:{' '}
        <strong>the student may then cancel at any time, with no notice at all, and it does not
        count against their record.</strong> They agreed to a particular day, and being asked about
        a different one is a new question they are entitled to answer no to. Both people are told
        when this happens.
      </p>

      {/* Added 19 August 2026 with multi-day bookings. Section 9 is the right
          home for it: a booking over several days changes what cancelling
          MEANS, and the promises here - three months, 24 hours, one day at a
          time - are all enforced in code. See MAX_REPEAT_MONTHS and
          canCancelVisit in backend/models/Chore.js. Do not write a limit into
          this page that the code does not keep. */}
      <h3>Jobs that run over several days</h3>
      <p>
        A job can cover more than one day - a dog walked every morning for a week, or babysitting
        every Wednesday and Saturday. A booking like this can run up to <strong>three months
        ahead</strong> and can hold at most <strong>100 visits</strong>. Nothing on {APP_NAME}
        repeats without an end date.
      </p>
      <p>
        The pay shown is <strong>per visit</strong>, and the total for the whole booking is shown
        alongside it. A student sees every date before agreeing, and agrees to the whole set rather
        than to part of it.
      </p>
      <p>
        <strong>Either side can drop a single day without ending the booking.</strong> A student
        must do so at least 24 hours before that day, the same notice as any other job; a poster can
        do so at any time, because dropping a day releases the student rather than leaving them
        short. The other person is told which day and why. The rest of the booking carries on
        unchanged.
      </p>
      <p>
        Dropping one day is not the same as leaving the job. A student who wants to end the whole
        arrangement cancels it in the ordinary way, under the rules above - and a student who does
        not feel safe can always leave immediately, at any point in a booking, under section 7.
      </p>

      {/* ============================================================= */}
      <h2>10. Payment, tax and social security</h2>
      <p>
        <strong>Payment is agreed and handled directly between the poster and the student.</strong>{' '}
        We never hold it, transfer it, guarantee it or take a share of it. Agree the amount before
        the work starts.
      </p>
      <p>
        <strong>We are not involved in any dispute about payment</strong> and cannot recover money
        for you, hold it in escrow, or decide who is right.
      </p>
      <p>
        Occasional domestic work in Italy can be paid legitimately through INPS&apos;s{' '}
        <em>Libretto Famiglia</em>, which covers exactly this kind of work - gardening, cleaning,
        childcare, tutoring - and includes INAIL accident cover for the student.{' '}
        <strong>We strongly recommend using it. We do not require it and we do not check
        it.</strong>
      </p>
      <p>
        <strong>Meeting any obligation for tax, social security, INPS, INAIL, insurance or the
        right to work is entirely a matter for the poster and the student.</strong> We give no
        advice on it, take no responsibility for it, and do not check whether anybody has done it.
        If you are unsure, ask a commercialista.
      </p>

      {/* Added 16 September 2026, when donations were switched on. The rules
          here are the ones in DONATIONS_PLAN.md section 2, stated to the
          person giving: it buys nothing, it is one-off, Stripe takes it. */}
      <h3>Donations</h3>
      <p>
        An adult poster may choose to give a donation towards the cost of running {APP_NAME}.
        Students are never asked and cannot donate.
      </p>
      <ul style={{ marginLeft: 'var(--space-md)' }}>
        <li>
          <strong>A donation is a gift, not a payment for anything.</strong> It buys no feature,
          badge, priority or standing, it is never shown to anybody else, and it makes no difference
          to how the site treats you or anybody you deal with.
        </li>
        <li>
          <strong>It is one-off.</strong> Nothing renews, and there is nothing to cancel.
        </li>
        <li>
          <strong>Stripe takes the payment</strong>, handles your card details and emails you the
          receipt. We never see your card.
        </li>
        <li>
          <strong>If you give by mistake</strong> - the wrong amount, or twice - write to us and we
          will refund it. Otherwise donations are not refunded.
        </li>
      </ul>

      {/* ============================================================= */}
      <h2>11. Moderation, suspension and removal</h2>
      <p>
        We may, at our discretion and without being asked, do any of the following where we
        reasonably believe these terms have been broken or somebody is at risk:
      </p>
      <ul style={{ marginLeft: 'var(--space-md)' }}>
        <li>
          <strong>Take down a chore or an offer.</strong> We will tell the person who posted it and
          give a reason.
        </li>
        <li>
          <strong>Hide a message</strong> from the two people in a conversation. The message itself
          is kept.
        </li>
        <li>
          <strong>Remove a review</strong> that breaks section 5.
        </li>
        <li>
          <strong>Suspend an account for a fixed period</strong> - anything from a day to a year.
          You will be told why and when it ends, and you will be signed out everywhere.
        </li>
        <li>
          <strong>Suspend an account indefinitely</strong>, where what happened is serious enough.
        </li>
      </ul>
      <p>
        We will normally explain and, where it is safe and sensible to do so, give you a chance to
        respond. <strong>We may act first and explain afterwards where somebody may be at
        risk</strong>, and we think that is the right order.
      </p>
      <p>
        If you think a decision was wrong, reply to the message telling you about it, or write to{' '}
        <a href={`mailto:${SAFETY_EMAIL}`}>{SAFETY_EMAIL}</a>. A person will look at it again.
      </p>

      {/* ============================================================= */}
      <h2>12. Availability of the site</h2>
      <p>
        We provide {APP_NAME} as it is. <strong>We do not promise it will be available, working,
        uninterrupted, secure or free of errors</strong>, and we may change, suspend or stop any
        part of it at any time. It is run by one person and it is free.
      </p>
      <p>
        We do not guarantee that anything on the site is accurate, complete or current - including
        chore descriptions, profiles, availability, distances and reviews, all of which come from
        users.
      </p>

      {/* ============================================================= */}
      <h2>13. Safety and reporting</h2>
      <p>
        <strong>If somebody is in immediate danger, call 112.</strong> Do that first and contact us
        afterwards.
      </p>
      <p>
        Otherwise, use the Report button on any profile, chore or conversation, or write to{' '}
        <a href={`mailto:${SAFETY_EMAIL}`}>{SAFETY_EMAIL}</a>. We read every report, and
        <strong> a moderator reads them at 20:00 every day</strong>. Nobody is watching the site
        overnight, which is why the line above about 112 comes first. Say what happened and when,
        and we will tell you what we have done where we can.
      </p>
      <p>
        You can also <strong>block</strong> another user at any time, without giving a reason and
        without them being told.
      </p>
      <p>
        <strong>&ldquo;Report an issue&rdquo; is a different thing, and is not for this.</strong> It
        is for the website misbehaving - a button that does nothing, a page that will not load. It
        reaches the same people, but it is not treated as a safety report and is not put in the
        queue as one. If somebody has made you uncomfortable, use the Report button above.
      </p>

      {/* ============================================================= */}
      <h2>14. Closing your account</h2>
      <p>
        You may close your account at any time from your settings. A parent or guardian may withdraw
        their permission at any time, which stops their child using the site for work.
      </p>
      <p>
        Closing an account removes your personal details. Chores, reviews and messages remain, with
        your name replaced, because deleting them would take other people&apos;s records with them.
        The <Link to="/privacy">privacy notice</Link> explains this in full.
      </p>

      {/* ============================================================= */}
      <h2>15. Liability</h2>
      <p>
        This section says what we are and are not responsible for.{' '}
        <strong>Nothing in it takes away rights you have as a consumer under Italian law.</strong>
      </p>
      <p>
        <strong>We are not responsible for what happens between users.</strong> To the fullest
        extent the law allows, that includes:
      </p>
      <ul style={{ marginLeft: 'var(--space-md)' }}>
        <li>injury, illness or death occurring during or because of a chore</li>
        <li>damage to, or loss or theft of, anybody&apos;s property</li>
        <li>the quality, safety or lawfulness of any work</li>
        <li>whether anybody is paid, paid on time, or paid the right amount</li>
        <li>anything anybody says or does, on the site or in person</li>
        <li>anybody being who they said they were</li>
        <li>
          any obligation for tax, social security, INPS, INAIL, insurance or the right to work
        </li>
        <li>anything a user posts, including anything untrue, offensive or unlawful</li>
      </ul>
      <p>
        <strong>What we are responsible for.</strong> We are responsible for running the site with
        reasonable care, for handling your information as our privacy notice describes, and for
        acting on reports. Where we fail at those and it causes you loss, we are responsible for
        that loss in the way the law provides.
      </p>
      <p>
        <strong>What cannot be limited, and is not.</strong> Nothing in these terms limits or
        excludes our liability for death or personal injury caused by our negligence, for fraud or
        fraudulent misrepresentation, for intentional wrongdoing or gross negligence, or for
        anything else that cannot lawfully be limited. Where any part of this section is found to go
        further than the law allows, it applies to the fullest extent that is lawful and the rest
        stays in force.
      </p>

      {/* ============================================================= */}
      <h2>16. If you cause us loss</h2>
      <p>
        If somebody makes a claim against us because of something you did - breaking these terms,
        breaking the law, injuring somebody, or posting something you had no right to post - you
        agree to cover the reasonable cost of dealing with it, including legal costs.
      </p>
      <p>
        This does not apply where the claim was caused by something we did wrong, and it does not
        apply to anything you did not actually cause.
      </p>

      {/* ============================================================= */}
      <h2>17. Changes to these terms</h2>
      <p>
        We may change these terms. If a change matters, we will tell you and update the date at the
        top. Carrying on using the site after that means you accept the new version; if you do not,
        you can close your account.
      </p>
      <p>
        <strong>If a change materially affects a student under 18, we ask their parent or guardian
        again</strong> rather than relying on the answer they already gave.
      </p>

      {/* ============================================================= */}
      <h2>18. Law, courts and complaints</h2>
      <p>
        These terms are governed by <strong>Italian law</strong>.
      </p>
      <p>
        If you are a consumer, you may bring a claim in the courts where you live, and nothing here
        takes away the protection of the law of the country you live in. Otherwise the courts of
        Italy have jurisdiction.
      </p>
      <p>
        Before going to court, please write to us - most things are quicker to sort out directly. As
        a consumer you may also be able to use an out-of-court dispute resolution body under the
        Italian Consumer Code (Codice del Consumo).
      </p>

      {/* ============================================================= */}
      <h2>19. Anything else</h2>
      <ul style={{ marginLeft: 'var(--space-md)' }}>
        <li>
          <strong>If part of these terms cannot be enforced</strong>, the rest still applies.
        </li>
        <li>
          <strong>If we do not enforce something straight away</strong>, we have not given up the
          right to enforce it later.
        </li>
        <li>
          <strong>You may not transfer your account</strong> or your rights under these terms to
          anybody else.
        </li>
        <li>
          <strong>Sections 5, 10, 15, 16 and 18 continue to apply</strong> after your account is
          closed.
        </li>
        <li>
          <strong>These terms and the privacy notice are the whole agreement</strong> between us
          about {APP_NAME}.
        </li>
      </ul>

      <hr className="divider" />

      <p className="text-sm text-muted">
        See also our <Link to="/privacy">privacy notice</Link>. Questions about these terms:{' '}
        <a href={`mailto:${SAFETY_EMAIL}`}>{SAFETY_EMAIL}</a>.
      </p>
    </>
  );
}
